<!--导入修改费用-->
<template>
  <div class="flex-c">
    <el-button
      icon="el-icon-download"
      size="small"
      style="margin: 0 10px 10px 0"
      @click="downloadTemplate"
    >
      {{ $t('basicData.DownloadTheTemplate') }}
    </el-button>
    <upload-excel
      :on-success="handleImport"
      :before-upload="beforeUpload"
      :but-name="$t('receivablePayable.ImportModifyExpenses')"
    />
  </div>
</template>

<script>
import uploadExcel from '@/components/UploadExcel';
import {
  apiPayableSiteExpenseImportEditFee,
  apiPayableSiteExpenseTemplate,
  apiPayableTransportExpenseDownloadImportTemplate,
  apiPayableTransportExpenseDownloadImportEditFee
} from '@/api/finance/receivables';
import { goFileUploadManagement } from '@/utils/goExportList.js';
import { saveAs } from 'file-saver';
import i18n from '@/lang';

const MAX_LENGTH = 100000;
const GROUP_SIZE = 2; // 费用字段分组大小（费用 + 备注）

// 账单类型对应的文件名
const FILE_NAME_MAP = {
  1: i18n.t('receivablePayable.AnjunCottage'), // 安俊
  2: i18n.t('receivablePayable.ThirdPartyCollection'), // 第三方
  3: i18n.t('receivablePayable.AJDDispatch'), // AJD
  4: i18n.t('receivablePayable.DSPDispatch'), // DSP
  5: i18n.t('receivablePayable.Transportation_trunk_branch'), // 干支线
  6: i18n.t('receivablePayable.postOfficeDelivery'), // 邮局派送
  7: i18n.t('receivablePayable.MDDDispatch') // MDD
};

// 基础字段映射
const BASE_FIELD_MAP = ['waybillNumber', 'businessType', 'chargeWeight'];
const BASE_FIELD_MAP_YS = ['transportNumber'];

// 费用项字段映射
const FEE_FIELD_MAP = ['fee', 'remark'];

/**
 * 判断值是否有效（非空）
 * @param {*} value 待检查的值
 * @returns {boolean}
 */
function isValidValue(value) {
  return value !== null && value !== undefined && value !== '';
}

/**
 * 安全获取字符串值
 * @param {*} value 原始值
 * @returns {string}
 */
function toSafeString(value) {
  return isValidValue(value) ? String(value).trim() : '';
}

export default {
  name: 'ImportEditFees',

  components: {
    uploadExcel
  },

  props: {
    calculationType: {
      type: Number,
      default: 2
    },
    payableType: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      feeTypeMap: null,
      fileName: ''
    };
  },

  methods: {
    /**
     * 过滤有效的表头字段（排除UNKNOWN字段）
     * @param {Array} headers 原始表头数组
     * @returns {Array}
     */
    filterValidHeaders(headers) {
      return headers.filter(item => !item.includes('UNKNOWN'));
    },

    /**
     * 将数组按指定长度分组
     * @param {Array} arr 原始数组
     * @param {number} size 每组大小
     * @returns {Array}
     */
    chunkArray(arr, size) {
      const result = [];
      for (let i = 0; i < arr.length; i += size) {
        result.push(arr.slice(i, i + size));
      }
      return result;
    },

    /**
     * 构建费用类型映射表
     * @param {Object} feeTypeRow 费用类型行数据
     * @returns {Object}
     */
    buildFeeTypeMap(feeTypeRow) {
      const map = {};
      Object.entries(feeTypeRow).forEach(([key, value]) => {
        map[value] = key;
      });
      return map;
    },

    /**
     * 构建表头字段映射
     * @param {Array} headerGroups 分组后的表头
     * @returns {Array}
     */
    buildHeaderFieldMap(headerGroups) {
      return headerGroups.map(group => {
        const fieldMap = {};
        group.forEach((header, index) => {
          if (FEE_FIELD_MAP[index]) {
            fieldMap[header] = FEE_FIELD_MAP[index];
          }
        });
        return fieldMap;
      });
    },

    /**
     * 解析单行数据为标准格式
     * @param {Object} rowData 原始行数据
     * @param {Array} validHeaders 有效表头
     * @param {Array} headerFieldMaps 表头字段映射
     * @param {Object} feeTypeRow 费用类型行
     * @returns {Object}
     */
    parseRowData(rowData, validHeaders, headerFieldMaps, feeTypeRow) {
      const result = {
        businessType: this.extractBusinessType(rowData[validHeaders[1]]),
        chargeWeight: toSafeString(rowData[validHeaders[2]]),
        waybillNumber: toSafeString(rowData[validHeaders[0]]),
        priceItemList: []
      };

      // 解析费用项
      headerFieldMaps.forEach(fieldMap => {
        const feeItem = this.parseFeeItem(rowData, fieldMap, feeTypeRow);
        if (feeItem) {
          result.priceItemList.push(feeItem);
        }
      });

      return this.payableType === 5 ? {
        transportNumber: toSafeString(rowData[validHeaders[0]]),
        priceItemList: result.priceItemList
      } : result;
    },

    /**
     * 提取业务类型
     * @param {string} value 原始值
     * @returns {string}
     */
    extractBusinessType(value) {
      if (!value) return '';
      const parts = String(value).split('-');
      return parts[1] || '';
    },

    /**
     * 解析费用项
     * @param {Object} rowData 行数据
     * @param {Object} fieldMap 字段映射
     * @param {Object} feeTypeRow 费用类型行
     * @returns {Object|null}
     */
    parseFeeItem(rowData, fieldMap, feeTypeRow) {
      let fee = '';
      let feeCode = '';
      let remark = '';

      Object.entries(fieldMap).forEach(([header, field]) => {
        const value = rowData[header];
        if (field === 'fee') {
          fee = toSafeString(value);
          feeCode = toSafeString(feeTypeRow[header]);
        } else if (field === 'remark') {
          remark = toSafeString(value);
        }
      });

      // 只有费用或备注有值时才添加
      if (isValidValue(fee) || isValidValue(remark)) {
        return { fee, feeCode, remark };
      }
      return null;
    },

    /**
     * 校验导入数据
     * @param {Array} dataList 数据列表
     * @returns {boolean}
     */
    validateImportData(dataList) {
      if (dataList.length === 0) {
        this.$message.warning(this.$t('userManage.ImportDataIsEmpty'));
        return false;
      }
      if (dataList.length > MAX_LENGTH) {
        this.$message.warning(this.$t('receivablePayable.Only100000PiecesImport'));
        return false;
      }
      return true;
    },

    /**
     * 处理导入数据
     * @param {Array} excelData Excel解析后的数据
     */
    handleImport(excelData) {
      const [dataSheet, feeTypeSheet] = excelData;
      const feeTypeRow = feeTypeSheet.results[0];

      // 构建费用类型映射
      this.feeTypeMap = this.buildFeeTypeMap(feeTypeRow);

      // 过滤有效表头
      const validHeaders = this.filterValidHeaders(dataSheet.header);

      // 费用字段表头（排除前3个基础字段）
      const HEADER_MAP = this.payableType === 5 ? BASE_FIELD_MAP_YS : BASE_FIELD_MAP;
      const feeHeaders = validHeaders.slice(HEADER_MAP.length);
      const headerGroups = this.chunkArray(feeHeaders, GROUP_SIZE);
      const headerFieldMaps = this.buildHeaderFieldMap(headerGroups);

      // 解析数据行（跳过前2行表头）
      const dataRows = dataSheet.results.slice(2);
      const parsedData = dataRows.map(row =>
        this.parseRowData(row, validHeaders, headerFieldMaps, feeTypeRow)
      );

      // 校验数据
      if (!this.validateImportData(parsedData)) {
        return;
      }

      // 提交数据
      this.submitImportData(parsedData);
    },
    beforeUpload(file) {
      this.fileName = file.name;
      return true;
    },

    /**
     * 提交导入数据
     * @param {Array} dataList 解析后的数据列表
     */
    submitImportData(dataList) {
      console.log('提交导入数据:', dataList);
      const API = this.payableType === 5
        ? apiPayableTransportExpenseDownloadImportEditFee // 干支线
        : apiPayableSiteExpenseImportEditFee;
      const params = this.payableType === 5
        ? dataList
        : {
          list: dataList,
          payableType: this.payableType,
          fileName: this.fileName
        };
      API(params).then(res => {
        if (res.code === 200) {
          this.$message.success(this.$t('basicData.TheOperationSucceeded'));
          goFileUploadManagement(this);
          this.$emit('changeFeeList', true);
        }
      });
    },

    /**
     * 下载模板
     */
    downloadTemplate() {
      const fileName = `${FILE_NAME_MAP[this.payableType]}${this.$t('receivablePayable.ImportModifyExpenses')}.xlsx`;
      const API = this.payableType === 5
        ? apiPayableTransportExpenseDownloadImportTemplate // 干支线
        : apiPayableSiteExpenseTemplate;
      API(this.payableType).then(res => {
        saveAs(res.data, fileName);
      });
    }
  }
};
</script>

<style scoped>
.flex-c {
  display: flex;
  flex-wrap: wrap;
}
</style>
